import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, X, UsersThree, MagnifyingGlass } from "@phosphor-icons/react";
import { SkeletonRows } from "@/components/RegisterSkeleton";

const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

function CrewForm({ crew, workers, onClose, onSaved }) {
  const [form, setForm] = useState(crew || { name: "", foreman_id: "", member_ids: [], notes: "" });
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleMember = (id) => set("member_ids",
    form.member_ids.includes(id) ? form.member_ids.filter((x) => x !== id) : [...form.member_ids, id]);

  const save = async () => {
    setError("");
    if (!form.name.trim()) { setError("Crew name is required."); return; }
    const payload = { ...form, foreman_id: form.foreman_id || null };
    if (payload.foreman_id && !payload.member_ids.includes(payload.foreman_id))
      payload.member_ids = [payload.foreman_id, ...payload.member_ids];
    try {
      if (crew?.id) await api.put(`/crews/${crew.id}`, payload);
      else await api.post("/crews", payload);
      onSaved();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const visible = workers.filter((w) => !q || w.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={onClose}>
      <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[560px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="crew-form">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{crew ? "Edit Crew" : "New Crew"}</div>
            <h3 className="font-bold text-[20px] text-ink-primary leading-tight mt-0.5">{crew?.name || "New Crew"}</h3>
          </div>
          <button onClick={onClose} data-testid="crew-form-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="crew-form-error">{error}</div>}
          <div><label className={lbl}>Crew Name *</label>
            <input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Earthworks Crew A" data-testid="crew-name-input" /></div>
          <div><label className={lbl}>Foreman / Leading Hand</label>
            <select className={inp} value={form.foreman_id || ""} onChange={(e) => set("foreman_id", e.target.value)} data-testid="crew-foreman-select">
              <option value="">— None —</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select></div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={lbl + " mb-0"}>Members <span className="font-normal text-ink-tertiary">({form.member_ids.length} selected)</span></label>
              <input className="h-8 px-2 border border-border-control bg-white text-xs rounded-[7px] w-40 focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" placeholder="Filter workers…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crew-member-filter" />
            </div>
            <div className="border border-border rounded-[8px] max-h-72 overflow-auto divide-y divide-divider-row bg-white" data-testid="crew-member-list">
              {visible.map((w) => (
                <label key={w.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-accent-wash">
                  <input type="checkbox" checked={form.member_ids.includes(w.id)} onChange={() => toggleMember(w.id)}
                    className="h-4 w-4 accent-equipr-yellow" data-testid={`crew-member-${w.id}`} />
                  <span className="font-medium text-ink-primary">{w.name}</span>
                  {form.foreman_id === w.id && <span className="text-[10px] bg-equipr-yellow px-1 font-bold rounded-[3px]">FOREMAN</span>}
                  {(w.skills || []).slice(0, 2).map((s) => <span key={s} className="text-[10px] bg-surface-inset border border-border text-ink-tertiary px-1 rounded-[3px]">{s}</span>)}
                </label>
              ))}
              {visible.length === 0 && <p className="text-xs text-ink-tertiary px-3 py-3">No workers match.</p>}
            </div>
          </div>
          <div><label className={lbl}>Notes</label>
            <textarea className={inp + " h-24 py-1.5"} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-surface-panel border-t border-divider-inner shrink-0">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
          <button onClick={save} data-testid="crew-save-btn" className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px">Save Crew</button>
        </div>
      </div>
    </div>
  );
}

export default function Crews() {
  const { isWriter } = useAuth();
  const [crews, setCrews] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([api.get("/crews"), api.get("/operators")]);
      setCrews(c.data);
      setWorkers(w.data.filter((x) => x.employment_status !== "inactive"));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const nameOf = (id) => workers.find((w) => w.id === id)?.name || "—";
  const remove = async (c) => {
    if (!window.confirm(`Delete crew "${c.name}"? Workers themselves are not affected.`)) return;
    await api.delete(`/crews/${c.id}`);
    load();
  };

  const filtered = crews.filter((c) => !q || JSON.stringify(c).toLowerCase().includes(q.toLowerCase()) || (c.member_ids || []).some((id) => nameOf(id).toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="h-full flex flex-col bg-surface-page p-4">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
          <div>
            <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">Crews</h1>
            <p className="text-[12px] text-ink-tertiary">Named groups of workers — drag a whole crew onto a project in the Scheduler</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Crew or member…" data-testid="crews-search"
                className="w-64 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[9px] text-body focus:outline-none focus:ring-2 focus:ring-equipr-yellow/35" />
            </div>
            {isWriter && (
              <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-crew-btn"
                className="h-[42px] px-4 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
                <Plus size={15} weight="bold" /> New Crew
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-cell">
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface-sunken text-left border-b border-border">
                {["Crew", "Foreman", "Members", "Notes", ""].map((h, i) => (
                  <th key={h || i} className={`px-4 py-2.5 uppercase tracking-[0.1em] text-colhead ${i === 0 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={5} />
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-ink-tertiary" data-testid="crews-empty">
                  {q ? "No crews match your search." : "No crews yet — create one and drag it onto a project in the Scheduler."}</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} className="border-b border-divider-row hover:bg-accent-wash cursor-pointer" data-testid={`crew-row-${c.id}`}
                  onClick={() => isWriter && (setEditing(c), setFormOpen(true))}>
                  <td className="px-4 py-2.5 font-semibold text-ink-primary"><span className="inline-flex items-center gap-1.5"><UsersThree size={15} className="text-accent-ink" /> {c.name}</span></td>
                  <td className="px-4 py-2.5 text-ink-secondary">{c.foreman_id ? nameOf(c.foreman_id) : "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="flex flex-wrap gap-1">
                      {(c.member_ids || []).map((id) => (
                        <span key={id} className="inline-block bg-surface-inset border border-border text-ink-secondary text-[11px] font-semibold px-1.5 py-0.5 rounded-[4px]">{nameOf(id)}</span>
                      ))}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-tertiary">{c.notes || "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {isWriter && <>
                      <button onClick={(e) => { e.stopPropagation(); setEditing(c); setFormOpen(true); }} data-testid={`crew-edit-${c.id}`} className="text-ink-tertiary hover:text-ink-primary mr-2"><PencilSimple size={15} /></button>
                      <button onClick={(e) => { e.stopPropagation(); remove(c); }} data-testid={`crew-delete-${c.id}`} className="text-ink-tertiary hover:text-red-600"><Trash size={15} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
          <span data-testid="crews-footer">{loading ? "Loading…" : `${filtered.length} crew${filtered.length === 1 ? "" : "s"}${q ? ` · filtered from ${crews.length}` : ""}`}</span>
        </div>
      </div>
      {formOpen && <CrewForm crew={editing} workers={workers} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}
